import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Heart, Globe, DollarSign, Users, TrendingUp, ArrowRight,
  Loader2, MapPin, Package, Flame, Quote, Camera, ChevronRight,
  Truck, MapPinned, Handshake, CheckCircle
} from 'lucide-react';
import axios from 'axios';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Mini sparkline for impact stat cards (light theme variant).
// Spec: keep on desktop, hide on mobile so the mobile Impact page
// stays clean. Data itself is REAL (30-day donation activity from
// /api/public/impact-stats), so it's information-preserving.
const ImpactSparkline = ({ data, color = '#BA1826' }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data, 1);
  const h = 24;
  const points = data.map((v, i) => `${(i / (data.length - 1)) * 100},${h - (v / max) * (h - 4) - 2}`).join(' ');
  const areaPoints = `0,${h} ${points} 100,${h}`;
  return (
    <div className="group/spark relative hidden md:block" title="Shows donation activity over the last 30 days">
      <svg viewBox={`0 0 100 ${h}`} preserveAspectRatio="none" className="w-full max-w-[100px] md:max-w-[120px] h-6 mx-auto mt-2 opacity-60">
        <polyline points={areaPoints} fill={color} fillOpacity="0.12" stroke="none" />
        <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      </svg>
      <span className="text-[#6B7280] text-[8px] md:text-[9px] mt-0.5 block text-center">30-day trend</span>
    </div>
  );
};

const ImpactPage = () => {
  const [impactStats, setImpactStats] = useState(null);
  const [globalImpact, setGlobalImpact] = useState(null);
  const [eventImpact, setEventImpact] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [impactGalleries, setImpactGalleries] = useState([]);
  // Distribution data — live from /api/public/distributions (fed by
  // Admin → Updates with type=distribution). Never a stored counter.
  const [upcomingDistribution, setUpcomingDistribution] = useState(null);
  const [recentDistributions, setRecentDistributions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, globalRes, eventRes, testRes, updatesRes, galRes, upcomingRes, recentRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/public/impact-stats`).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/public/global-impact`).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/public/events/impact-totals`).catch(() => ({ data: {} })),
        axios.get(`${BACKEND_URL}/api/public/testimonials`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/public/updates/recent`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/public/galleries/impact`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/public/distributions?status=upcoming&limit=1`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/public/distributions?status=completed&limit=2`).catch(() => ({ data: [] })),
      ]);
      setImpactStats(statsRes.data);
      setGlobalImpact(globalRes.data);
      setEventImpact(eventRes.data);
      setTestimonials(testRes.data?.slice(0, 6) || []);
      setUpdates(updatesRes.data?.slice(0, 4) || []);
      setImpactGalleries(galRes.data || []);
      setUpcomingDistribution((upcomingRes.data || [])[0] || null);
      setRecentDistributions(recentRes.data || []);
    } catch (error) {
      logger.error('Error fetching impact data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#BA1826]" />
      </div>
    );
  }

  const totalRaised = impactStats?.total_amount || 0;
  const familiesServed = impactStats?.families_served || 0;
  const totalDonations = impactStats?.total_donations || 0;
  const activeCountries = globalImpact?.active_countries || 0;
  const globalPrograms = globalImpact?.programs || [];
  const totalAttendees = eventImpact?.total_attendees || 0;
  const totalItemsCollected = eventImpact?.total_items_collected || 0;
  const trend = impactStats?.trend || [];
  // Distribution stats live-computed on the backend from community_updates
  // where type=distribution + status=completed + is_published. Never a
  // stored counter — edit qty 30→35 shifts totals by +5, not +35.
  const distStats = impactStats?.distributions || {};
  const itemsDistributed = distStats.items_distributed || 0;
  const distributionsCompleted = distStats.distributions_completed || 0;
  const locationsReached = distStats.locations_reached || 0;
  const partnersReached = distStats.partners_reached || 0;

  return (
    <div>
      {/* Hero */}
      <section className="bg-[#0A0A0A] py-16 relative overflow-hidden" data-testid="impact-hero">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z' fill='none' stroke='white' stroke-width='1'/%3E%3C/svg%3E")`,
        }} />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-[#BA1826] font-semibold mb-3">Our Impact</p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight" data-testid="impact-page-title">
            Every Dollar Creates<br />Real Change
          </h1>
          <p className="text-gray-400 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
            We believe in full transparency. Here's exactly where your support goes and the real difference it makes in communities around the world.
          </p>
        </div>
      </section>

      {/* Impact Tracking */}
      <section className="py-14 bg-white" data-testid="impact-tracking-section">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.2em] text-[#BA1826] font-semibold mb-2">Impact Tracking</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-2">
              Measurable Progress, Real Results
            </h2>
            <p className="text-sm text-[#4B5563] max-w-lg mx-auto">
              Every contribution is tracked and every result is measured. These numbers represent real lives touched.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="impact-stats-grid">
            <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl p-5 md:p-6 text-center group hover:border-[#BA1826]/30 transition-colors">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-[#FEE2E2] rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3">
                <DollarSign className="w-4 h-4 md:w-5 md:h-5 text-[#BA1826]" />
              </div>
              <div className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-[#0A0A0A] mb-1" data-testid="stat-total-raised">
                ${totalRaised.toLocaleString()}
              </div>
              <p className="text-[10px] md:text-xs text-[#4B5563] font-medium">Total Raised</p>
              <ImpactSparkline data={trend.map(d => d.amount)} />
            </div>

            <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl p-5 md:p-6 text-center group hover:border-[#BA1826]/30 transition-colors">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-[#FEE2E2] rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3">
                <Users className="w-4 h-4 md:w-5 md:h-5 text-[#BA1826]" />
              </div>
              <div className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-[#0A0A0A] mb-1" data-testid="stat-families-served">
                {familiesServed.toLocaleString()}
              </div>
              <p className="text-[10px] md:text-xs text-[#4B5563] font-medium">Families Served</p>
              <ImpactSparkline data={trend.map(d => d.families)} />
            </div>

            <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl p-5 md:p-6 text-center group hover:border-[#BA1826]/30 transition-colors">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-[#FEE2E2] rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3">
                <Heart className="w-4 h-4 md:w-5 md:h-5 text-[#BA1826]" />
              </div>
              <div className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-[#0A0A0A] mb-1" data-testid="stat-total-donations">
                {totalDonations.toLocaleString()}
              </div>
              <p className="text-[10px] md:text-xs text-[#4B5563] font-medium">Donations Made</p>
              <ImpactSparkline data={trend.map(d => d.donations)} />
            </div>

            <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl p-5 md:p-6 text-center group hover:border-[#BA1826]/30 transition-colors">
              <div className="w-10 h-10 md:w-12 md:h-12 bg-[#FEE2E2] rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3">
                <Globe className="w-4 h-4 md:w-5 md:h-5 text-[#BA1826]" />
              </div>
              <div className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-[#0A0A0A] mb-1" data-testid="stat-countries">
                {activeCountries}
              </div>
              <p className="text-[10px] md:text-xs text-[#4B5563] font-medium">Countries Reached</p>
            </div>
          </div>

          {/* Non-financial distribution metrics — only rendered when
              at least one completed distribution exists. Live from
              /api/public/impact-stats.distributions. */}
          {(itemsDistributed > 0 || distributionsCompleted > 0) && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4" data-testid="distribution-stats-grid">
              <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl p-5 md:p-6 text-center hover:border-[#BA1826]/30 transition-colors">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#FEE2E2] rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3">
                  <Package className="w-4 h-4 md:w-5 md:h-5 text-[#BA1826]" />
                </div>
                <div className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-[#0A0A0A] mb-1" data-testid="stat-items-distributed">
                  {itemsDistributed.toLocaleString()}
                </div>
                <p className="text-[10px] md:text-xs text-[#4B5563] font-medium">Essential Items Distributed</p>
              </div>
              <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl p-5 md:p-6 text-center hover:border-[#BA1826]/30 transition-colors">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#FEE2E2] rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3">
                  <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-[#BA1826]" />
                </div>
                <div className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-[#0A0A0A] mb-1" data-testid="stat-distributions-completed">
                  {distributionsCompleted.toLocaleString()}
                </div>
                <p className="text-[10px] md:text-xs text-[#4B5563] font-medium">Distributions Completed</p>
              </div>
              <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl p-5 md:p-6 text-center hover:border-[#BA1826]/30 transition-colors">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#FEE2E2] rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3">
                  <MapPinned className="w-4 h-4 md:w-5 md:h-5 text-[#BA1826]" />
                </div>
                <div className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-[#0A0A0A] mb-1" data-testid="stat-locations-reached">
                  {locationsReached.toLocaleString()}
                </div>
                <p className="text-[10px] md:text-xs text-[#4B5563] font-medium">Community Locations</p>
              </div>
              <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl p-5 md:p-6 text-center hover:border-[#BA1826]/30 transition-colors">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-[#FEE2E2] rounded-full flex items-center justify-center mx-auto mb-2 md:mb-3">
                  <Handshake className="w-4 h-4 md:w-5 md:h-5 text-[#BA1826]" />
                </div>
                <div className="font-heading text-xl sm:text-2xl md:text-3xl font-bold text-[#0A0A0A] mb-1" data-testid="stat-partners-reached">
                  {partnersReached.toLocaleString()}
                </div>
                <p className="text-[10px] md:text-xs text-[#4B5563] font-medium">Community Partners</p>
              </div>
            </div>
          )}

          {/* Event impact stats if they exist */}
          {(totalAttendees > 0 || totalItemsCollected > 0) && (
            <div className="grid grid-cols-2 gap-4 mt-4 max-w-lg mx-auto">
              {totalAttendees > 0 && (
                <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl p-5 text-center">
                  <div className="font-heading text-xl font-bold text-[#0A0A0A] mb-1">{totalAttendees.toLocaleString()}</div>
                  <p className="text-xs text-[#4B5563]">Event Attendees</p>
                </div>
              )}
              {totalItemsCollected > 0 && (
                <div className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl p-5 text-center">
                  <div className="font-heading text-xl font-bold text-[#0A0A0A] mb-1">{totalItemsCollected.toLocaleString()}</div>
                  <p className="text-xs text-[#4B5563]">Items Collected</p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Your Donations at Work — compact 3-link version (spec).
          Previous large 3-card layout was replaced because the Impact
          numbers above already communicate measurable results. */}
      <section className="py-10 sm:py-12 bg-[#F8F9FA]" data-testid="donations-at-work-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-6 sm:mb-8">
            <p className="text-xs uppercase tracking-[0.2em] text-[#BA1826] font-semibold mb-2">Transparency</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A]">
              Your Donations at Work
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Link to="/programs"
              className="bg-white border border-[#E5E7EB] rounded-lg px-4 py-3 flex items-center justify-between hover:border-[#BA1826]/40 hover:shadow-sm transition-all group"
              data-testid="donation-use-supplies">
              <div className="flex items-center gap-3 min-w-0">
                <Package className="w-4 h-4 text-[#BA1826] flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-heading text-sm font-bold text-[#0A0A0A] leading-tight">Essential Supplies</p>
                  <p className="text-[11px] text-[#6B7280] truncate">Food, hygiene & everyday essentials</p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-[#9CA3AF] group-hover:text-[#BA1826] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
            </Link>

            <Link to="/programs"
              className="bg-white border border-[#E5E7EB] rounded-lg px-4 py-3 flex items-center justify-between hover:border-[#BA1826]/40 hover:shadow-sm transition-all group"
              data-testid="donation-use-programs">
              <div className="flex items-center gap-3 min-w-0">
                <Globe className="w-4 h-4 text-[#BA1826] flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-heading text-sm font-bold text-[#0A0A0A] leading-tight">Community Programs</p>
                  <p className="text-[11px] text-[#6B7280] truncate">Local and global community support</p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-[#9CA3AF] group-hover:text-[#BA1826] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
            </Link>

            <Link to="/events"
              className="bg-white border border-[#E5E7EB] rounded-lg px-4 py-3 flex items-center justify-between hover:border-[#BA1826]/40 hover:shadow-sm transition-all group"
              data-testid="donation-use-events">
              <div className="flex items-center gap-3 min-w-0">
                <Flame className="w-4 h-4 text-[#BA1826] flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-heading text-sm font-bold text-[#0A0A0A] leading-tight">Events & Outreach</p>
                  <p className="text-[11px] text-[#6B7280] truncate">Fundraisers, outreach & community</p>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-[#9CA3AF] group-hover:text-[#BA1826] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
            </Link>
          </div>
        </div>
      </section>

      {/* Global Impact Across Programs */}
      {globalPrograms.length > 0 && (
        <section className="py-14 bg-white" data-testid="global-impact-section">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-10">
              <p className="text-xs uppercase tracking-[0.2em] text-[#BA1826] font-semibold mb-2">Worldwide</p>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-2">
                Global Impact Across All Programs
              </h2>
              <p className="text-sm text-[#4B5563] max-w-lg mx-auto">
                From local neighborhoods to communities around the world, your support reaches people wherever they need it most.
              </p>
            </div>

            {/* Global summary */}
            {globalImpact && globalImpact.total_raised > 0 && (
              <div className="bg-[#0A0A0A] rounded-xl p-6 mb-8" data-testid="global-progress">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Global Progress</p>
                    <p className="font-heading text-xl font-bold text-white">
                      ${globalImpact.total_raised.toLocaleString()}
                      <span className="text-sm text-gray-400 font-normal"> / ${globalImpact.total_monthly_goal.toLocaleString()} goal</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-heading text-xl font-bold text-[#BA1826]">{Math.round(globalImpact.progress_percentage)}%</p>
                  </div>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2.5">
                  <div className="bg-[#BA1826] h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(globalImpact.progress_percentage, 100)}%` }} />
                </div>
                {globalImpact.total_families_supported > 0 && (
                  <p className="text-xs text-gray-400 mt-2">
                    {globalImpact.total_families_supported} families supported across {activeCountries} countries
                  </p>
                )}
              </div>
            )}

            {/* Country cards */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {globalPrograms.map((p) => {
                const progress = p.monthly_goal > 0 ? Math.min((p.amount_raised / p.monthly_goal) * 100, 100) : 0;
                return (
                  <Link
                    to={`/international/${p.id}`}
                    key={p.id}
                    className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-xl p-5 hover:border-[#BA1826]/30 hover:shadow-sm transition-all group"
                    data-testid={`program-card-${p.id}`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-[#BA1826]" />
                        <h3 className="font-heading text-sm font-bold text-[#0A0A0A]">{p.country_name}</h3>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[#9CA3AF] group-hover:text-[#BA1826] transition-colors" />
                    </div>
                    {p.program_city && (
                      <p className="text-xs text-[#4B5563] mb-2">{p.program_city}</p>
                    )}
                    <div className="w-full bg-[#E5E7EB] rounded-full h-1.5 mb-2">
                      <div className="bg-[#BA1826] h-1.5 rounded-full" style={{ width: `${progress}%` }} />
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#4B5563]">${p.amount_raised.toLocaleString()} raised</span>
                      <span className="text-[#9CA3AF]">${p.monthly_goal.toLocaleString()} goal</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Impact Photo Gallery */}
      {impactGalleries.length > 0 && (
        <section className="py-14 bg-white" data-testid="impact-gallery-section">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-10">
              <p className="text-xs uppercase tracking-[0.2em] text-[#BA1826] font-semibold mb-2">In the Field</p>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-2">
                Impact in Action
              </h2>
              <p className="text-sm text-[#4B5563] max-w-lg mx-auto">
                Real photos from our community work — captured by our team and the communities we serve.
              </p>
            </div>
            {impactGalleries.map((g) => (
              <div key={g.id} className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Camera className="w-4 h-4 text-[#BA1826]" />
                  <h3 className="font-heading text-base font-bold text-[#0A0A0A]">{g.event_title}</h3>
                  {g.event_date && <span className="text-xs text-[#9CA3AF]">{g.event_date}</span>}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  {g.photos.slice(0, 12).map((p) => (
                    <div key={p.id} className="aspect-square rounded-lg overflow-hidden bg-[#F1F5F9]">
                      <img src={p.image_url} alt={p.guest_name || 'Impact photo'} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" loading="lazy" />
                    </div>
                  ))}
                </div>
                {g.photos.length > 12 && (
                  <Link to={`/gallery/${g.id}/upload`} className="text-xs text-[#BA1826] font-semibold hover:underline mt-2 inline-block">
                    View all {g.photos.length} photos
                  </Link>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Community Stories / Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-14 bg-[#0A0A0A]" data-testid="community-stories-section">
          <div className="max-w-5xl mx-auto px-6">
            <div className="text-center mb-10">
              <p className="text-xs uppercase tracking-[0.2em] text-[#BA1826] font-semibold mb-2">Voices</p>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-2">
                Stories From Our Community
              </h2>
              <p className="text-sm text-gray-400 max-w-lg mx-auto">
                Real words from real people whose lives have been touched by this foundation's work.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-white/5 border border-white/10 rounded-xl p-5" data-testid={`testimonial-${t.id}`}>
                  <Quote className="w-5 h-5 text-[#BA1826] mb-3" />
                  <p className="text-sm text-gray-300 leading-relaxed mb-4 line-clamp-5">{t.quote}</p>
                  <div className="border-t border-white/10 pt-3">
                    <p className="text-xs font-semibold text-white">{t.name}</p>
                    {t.role && <p className="text-[10px] text-gray-500">{t.role}</p>}
                    {t.country && <p className="text-[10px] text-[#BA1826]">{t.country}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Distribution & Community Impact — replaces the old
          community-updates "From the Field" block. Data comes from
          /api/public/distributions (Admin → Updates with type=
          distribution). No large impact-number cards here; those live
          in the Measurable Progress section above. */}
      {(upcomingDistribution || recentDistributions.length > 0) && (
        <section className="py-12 sm:py-14 bg-[#F8F9FA]" data-testid="distributions-section">
          <div className="max-w-5xl mx-auto px-6">
            <div className="flex items-end justify-between mb-6 sm:mb-8 flex-wrap gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#BA1826] font-semibold mb-2">From the Field</p>
                <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A]">Distribution & Community Impact</h2>
              </div>
              <Link to="/distributions"
                className="text-xs font-semibold text-[#BA1826] hover:underline flex items-center gap-1"
                data-testid="view-all-distributions-link">
                View All Distributions <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            {/* Upcoming distribution — single compact card. Only rendered
                when there's a public upcoming record. Does not contribute
                to completed impact totals (backend guarantees this). */}
            {upcomingDistribution && (
              <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 sm:p-5 mb-5 flex flex-col sm:flex-row gap-4 items-start"
                   data-testid="upcoming-distribution-card">
                <span className="text-[10px] font-bold uppercase tracking-wider bg-yellow-100 text-yellow-800 px-2 py-1 rounded flex-shrink-0">
                  Upcoming
                </span>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading text-base sm:text-lg font-bold text-[#0A0A0A] mb-1">
                    {upcomingDistribution.title}
                  </h3>
                  <div className="text-xs text-[#4B5563] flex flex-wrap gap-x-3 gap-y-1 mb-2">
                    {upcomingDistribution.date && <span>{upcomingDistribution.date}</span>}
                    {upcomingDistribution.location && (
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{upcomingDistribution.location}</span>
                    )}
                    {upcomingDistribution.partner && <span>Partner: {upcomingDistribution.partner}</span>}
                  </div>
                  {upcomingDistribution.description && (
                    <p className="text-xs text-[#6B7280] line-clamp-2">{upcomingDistribution.description}</p>
                  )}
                </div>
              </div>
            )}

            {/* Two most recent completed distributions */}
            {recentDistributions.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-4" data-testid="recent-distributions-grid">
                {recentDistributions.map((d) => (
                  <Link
                    to={`/distributions/${d.id}`}
                    key={d.id}
                    className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden hover:border-[#BA1826]/40 hover:shadow-sm transition-all group"
                    data-testid={`distribution-card-${d.id}`}
                  >
                    {d.image_url ? (
                      <div className="h-40 bg-[#F1F5F9] overflow-hidden">
                        <img src={d.image_url} alt={d.title} loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                      </div>
                    ) : (
                      <div className="h-14 bg-[#F1F5F9] flex items-center justify-center">
                        <Truck className="w-5 h-5 text-[#9CA3AF]" />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-[10px] font-bold uppercase tracking-wider bg-green-100 text-green-800 px-2 py-0.5 rounded">Completed</span>
                        {d.quantity > 0 && d.item_type && (
                          <span className="text-[11px] font-semibold text-[#0A0A0A]">
                            {d.quantity} {d.item_type}
                          </span>
                        )}
                      </div>
                      <h3 className="font-heading text-sm font-bold text-[#0A0A0A] mb-1 line-clamp-1">{d.title}</h3>
                      <div className="text-[11px] text-[#4B5563] flex flex-wrap gap-x-2">
                        {d.location && <span className="flex items-center gap-0.5"><MapPin className="w-3 h-3 text-[#BA1826]" />{d.location}</span>}
                        {d.date && <span>· {d.date}</span>}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Recent Community Updates — kept when there are non-distribution
          updates to share (community stories, health initiatives, etc.). */}
      {updates.filter(u => u.type !== 'distribution').length > 0 && (
        <section className="py-12 bg-white" data-testid="recent-updates-section">
          <div className="max-w-5xl mx-auto px-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-[#BA1826] font-semibold mb-2">Updates</p>
                <h2 className="font-heading text-2xl font-bold text-[#0A0A0A]">Community Updates</h2>
              </div>
              <Link to="/updates" className="text-xs font-semibold text-[#BA1826] hover:underline flex items-center gap-1" data-testid="view-all-updates-link">
                All Updates <ArrowRight className="w-3 h-3" />
              </Link>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              {updates.filter(u => u.type !== 'distribution').slice(0, 2).map((u) => (
                <div key={u.id} className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden hover:shadow-sm transition-shadow" data-testid={`update-card-${u.id}`}>
                  {u.image_url && (
                    <div className="h-40">
                      <img src={u.image_url} alt={u.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      {u.category && (
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-[#BA1826]">{u.category}</span>
                      )}
                      {u.date && (
                        <span className="text-[10px] text-[#9CA3AF]">{u.date}</span>
                      )}
                    </div>
                    <h3 className="font-heading text-sm font-bold text-[#0A0A0A] mb-2">{u.title}</h3>
                    <p className="text-xs text-[#4B5563] line-clamp-3 leading-relaxed">{u.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Emotional CTA */}
      <section className="py-16 bg-[#BA1826] relative overflow-hidden" data-testid="impact-cta">
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z' fill='none' stroke='white' stroke-width='1'/%3E%3C/svg%3E")`,
        }} />
        <div className="relative max-w-2xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
            Be Part of the Next Chapter
          </h2>
          <p className="text-white/85 text-sm sm:text-base leading-relaxed mb-8 max-w-xl mx-auto">
            Behind every number is a family that received a meal, a child who felt cared for, a community that found hope.
            Your support writes the next page of their story.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/donate" className="bg-white text-[#BA1826] px-8 py-3 font-bold rounded hover:bg-gray-100 transition-all text-sm" data-testid="impact-donate-btn">
              Make a Donation
            </Link>
            <Link to="/events" className="bg-transparent text-white border-2 border-white px-8 py-3 font-bold rounded hover:bg-white/10 transition-all text-sm" data-testid="impact-events-btn">
              Attend an Event
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ImpactPage;
